/**
 * The student profile's tabs, in order, and which of them this viewer sees.
 *
 * A tab with `section: null` is core and always shown. Any other tab is shown
 * only when the server lists its `section` in `profile.sections` (GET
 * /api/organisations/students/<id>), which
 * the server works out from the viewer's permissions and the school's
 * features. Nothing here, or in a panel, decides access itself.
 *
 * Every panel is rendered with the same props:
 * { studentId, currentUserId, profile, period, onPeriodChange,
 *   onCommentPosted, onOpenPiece, onOpenPaper, paperHref, onOpenQuiz,
 *   quizHref, section, onSectionChange, tracker }.
 */
import React from 'react';
import StudentActivityPanel from './StudentActivityPanel';
import StudentDocumentsPanel from './StudentDocumentsPanel';
import StudentFeedbackPanel from './StudentFeedbackPanel';
import StudentOtherDetailsPanel from './StudentOtherDetailsPanel';
import StudentSchoolRecordPanel from './StudentSchoolRecordPanel';

const ActivityTab = ({
  studentId, period, onPeriodChange, onCommentPosted, onOpenPaper, paperHref, onOpenQuiz, quizHref,
  section, onSectionChange,
}) => (
  <StudentActivityPanel
    studentId={studentId}
    period={period}
    onPeriodChange={onPeriodChange}
    onCommentPosted={onCommentPosted}
    onOpenPaper={onOpenPaper}
    paperHref={paperHref}
    onOpenQuiz={onOpenQuiz}
    quizHref={quizHref}
    section={section}
    onSectionChange={onSectionChange}
  />
);

const DocumentsTab = ({ tracker }) => (
  <StudentDocumentsPanel
    documents={tracker.data?.documents}
    loading={tracker.loading}
    forbidden={tracker.forbidden}
    error={tracker.error}
  />
);

const FeedbackTab = ({ tracker, currentUserId, onOpenPiece }) => (
  <StudentFeedbackPanel
    comments={tracker.data?.comments}
    loading={tracker.loading}
    forbidden={tracker.forbidden}
    error={tracker.error}
    currentUserId={currentUserId}
    posting={tracker.posting}
    onPostComment={tracker.onPostComment}
    onOpenPiece={onOpenPiece}
  />
);

// A new school extension is one entry here, with the `section` key the server
// sends for it, plus its panel. Nothing else changes.
export const PROFILE_SECTIONS = [
  { value: 'activity', label: 'Activity', section: null, Panel: ActivityTab },
  { value: 'school_record', label: 'School record', section: 'school_record', Panel: StudentSchoolRecordPanel },
  { value: 'documents', label: 'Documents', section: null, Panel: DocumentsTab },
  { value: 'feedback', label: 'Feedback', section: null, Panel: FeedbackTab },
  { value: 'sensitive_details', label: 'Other details', section: 'sensitive_details', Panel: StudentOtherDetailsPanel },
];

/** The tabs to show for a profile response. Without `sections` (an older backend), only the core tabs. */
export function visibleSections(profile) {
  const granted = Array.isArray(profile?.sections) ? profile.sections : [];
  return PROFILE_SECTIONS.filter((s) => s.section === null || granted.includes(s.section));
}
